import React, { useState } from 'react';
import Layout from '../../components/admin/Layout';
import DataTable from '../../components/admin/DataTable';
import DetailDrawer from '../../components/admin/DetailDrawer';
import { Button, Field, StatusBadge, formatDate, relativeTime } from '../../components/admin/ui';
import useAdminResource from '../../hooks/useAdminResource';

const STATUSES = ['new', 'contacted', 'scheduled', 'completed', 'archived'];

// `datetime-local` needs `YYYY-MM-DDTHH:mm` in local time.
const toLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const Consultations = () => {
  const controller = useAdminResource({ resource: 'consultations' });
  const [active, setActive] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');

  const openRow = (row) => {
    setActive(row);
    setScheduledAt(toLocalInput(row.scheduledAt));
  };

  const applyStatus = async (status) => {
    if (!active) return;
    const ok = await controller.updateRow(active.id, { status }, 'Consultation updated');
    if (ok) setActive({ ...active, status });
  };

  const saveSchedule = async () => {
    if (!active) return;
    const iso = scheduledAt ? new Date(scheduledAt).toISOString() : null;
    // Booking a slot moves it to "scheduled" unless it is already further along.
    const status = ['new', 'contacted'].includes(active.status) && iso ? 'scheduled' : active.status;
    const ok = await controller.updateRow(active.id, { scheduledAt: iso, status }, 'Schedule saved');
    if (ok) setActive({ ...active, scheduledAt: iso, status });
  };

  const columns = [
    {
      key: 'name',
      label: 'Requested by',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-900">{row.name}</p>
          <a
            href={`mailto:${row.email}`}
            onClick={(e) => e.stopPropagation()}
            className="block truncate text-xs text-indigo-600 hover:underline"
          >
            {row.email}
          </a>
        </div>
      )
    },
    {
      key: 'company',
      label: 'Company',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-slate-900">{row.company || '—'}</p>
          {row.phone && <p className="mt-0.5 truncate text-xs text-slate-500">{row.phone}</p>}
        </div>
      )
    },
    {
      key: 'service',
      label: 'Service',
      render: (row) => (row.service ? (
        <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          {row.service}
        </span>
      ) : <span className="text-slate-300">—</span>)
    },
    {
      key: 'scheduledAt',
      label: 'Scheduled',
      className: 'whitespace-nowrap',
      render: (row) => (row.scheduledAt
        ? <span className="text-slate-700">{formatDate(row.scheduledAt, true)}</span>
        : <span className="text-slate-300">Not set</span>)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      key: 'createdAt',
      label: 'Requested',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="text-slate-500" title={formatDate(row.createdAt, true)}>
          {relativeTime(row.createdAt)}
        </span>
      )
    }
  ];

  return (
    <Layout>
      <DataTable
        title="Consultations"
        description="Consultation requests from the website"
        icon="💬"
        controller={controller}
        columns={columns}
        statuses={STATUSES}
        bulkStatuses={STATUSES}
        onRowClick={openRow}
        searchPlaceholder="Search name, email, company, service…"
        emptyTitle="No consultation requests yet"
        emptyDescription="Requests from the consultation form will appear here."
      />

      <DetailDrawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.name || ''}
        subtitle={active?.company || 'Consultation request'}
        status={active?.status}
        statuses={STATUSES}
        onStatusChange={applyStatus}
        footer={active && (
          <>
            <Button
              variant="secondary"
              onClick={() => { window.location.href = `mailto:${active.email}`; }}
            >
              Email
            </Button>
            <Button variant="primary" onClick={saveSchedule}>Save schedule</Button>
          </>
        )}
      >
        {active && (
          <div className="space-y-6">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
              <Field label="Name">{active.name}</Field>
              <Field label="Requested">{formatDate(active.createdAt, true)}</Field>
              <Field label="Email">
                <a href={`mailto:${active.email}`} className="text-indigo-600 hover:underline">
                  {active.email}
                </a>
              </Field>
              <Field label="Phone">
                {active.phone && (
                  <a href={`tel:${active.phone}`} className="text-indigo-600 hover:underline">
                    {active.phone}
                  </a>
                )}
              </Field>
              <Field label="Company">{active.company}</Field>
              <Field label="Service">{active.service}</Field>
            </dl>

            {active.message && (
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Message
                </h3>
                <div className="whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                  {active.message}
                </div>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Appointment
              </h3>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full rounded-xl border-0 bg-slate-50 p-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 focus:bg-white focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-2 text-xs text-slate-400">
                Saving a date moves a new request to “scheduled”.
              </p>
            </section>
          </div>
        )}
      </DetailDrawer>
    </Layout>
  );
};

export default Consultations;
